import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Testimonial {
  id: string;
  author_name: string;
  author_role: string;
  author_company: string | null;
  author_image: string | null;
  content: string;
  rating: number;
  display_order: number;
}

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    async function fetchTestimonials() {
      try {
        const { data, error } = await supabase
          .from('testimonials')
          .select('*')
          .eq('is_active', true)
          .eq('is_featured', true)
          .order('display_order', { ascending: true });

        if (error) throw error;
        if (data) setTestimonials(data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    }

    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (testimonials.length <= 3) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        return prev >= testimonials.length - 3 ? 0 : prev + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => {
      return prev === 0 ? testimonials.length - 3 : prev - 1;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      return prev >= testimonials.length - 3 ? 0 : prev + 1;
    });
  };

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + 3);
  
  if (visibleTestimonials.length < 3 && testimonials.length >= 3) {
    const remaining = 3 - visibleTestimonials.length;
    visibleTestimonials.push(...testimonials.slice(0, remaining));
  }

  if (testimonials.length === 0) return null;

  return (
    <section className="py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Ils nous font confiance
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Découvrez comment nos clients ont transformé leur présence en ligne
          </p>
        </div>

        <div className="relative">
          {/* Flèches de navigation */}
          {testimonials.length > 3 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white hover:bg-slate-100 text-slate-800 w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer"
                aria-label="Avis précédent"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white hover:bg-slate-100 text-slate-800 w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer"
                aria-label="Avis suivant"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </>
          )}

          {/* Grille des témoignages avec animation */}
          <div className="overflow-hidden">
            <div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-700 ease-in-out"
              style={{
                transform: `translateX(0)`,
              }}
            >
              {visibleTestimonials.map((testimonial, index) => (
                <div 
                  key={`${testimonial.id}-${currentIndex}-${index}`}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
                  style={{
                    animation: 'fadeSlideIn 0.7s ease-out'
                  }}
                >
                  <div className="flex items-center mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-600 text-lg"></i>
                    ))}
                  </div>
                  
                  <p className="text-slate-700 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    {testimonial.author_image && (
                      <img 
                        src={testimonial.author_image}
                        alt={testimonial.author_name}
                        className="w-14 h-14 rounded-full object-cover object-top"
                      />
                    )}
                    <div>
                      <div className="font-bold text-slate-900">{testimonial.author_name}</div>
                      <div className="text-sm text-slate-600">{testimonial.author_role}</div>
                      {testimonial.author_company && (
                        <div className="text-sm text-yellow-700">{testimonial.author_company}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Points de navigation */}
          {testimonials.length > 3 && (
            <div className="flex justify-center mt-12 space-x-2">
              {Array.from({ length: testimonials.length - 2 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    currentIndex === index 
                      ? 'bg-yellow-600 w-8 h-2' 
                      : 'bg-slate-300 hover:bg-slate-400 w-2 h-2'
                  }`}
                  aria-label={`Aller au témoignage ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
}
